import { ModuleWithProviders, NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { SharedModule } from "./shared/shared.module";
import { CoreModule } from "./core/core.module";
import { NotAuthorizedComponent } from "./security/not-authorized/not-authorized.component";
import { BrowserModule } from "@angular/platform-browser";
import { LoginGuard } from './security/login.guard';
import { EnvironmentMarkerComponent } from "./layouts/environment-marker/environment-marker.component";
import { IntercapiLayoutComponent } from "./layouts/intercapi-layout/intercapi-layout.component";
import { IntercapiPublicLayoutComponent } from "./layouts/intercapi-public-layout/intercapi-public-layout.component";
import {
  PublikationenPublicLayoutComponent
} from "./layouts/publikationen-public-layout/publikationen-public-layout.component";
import { AuthorizationGuard } from "./security/authorization.guard";
import { ForbiddenComponent } from "./security/forbidden/forbidden.component";
import { UnsupportedComponent } from "./security/unsupported/unsupported.component";
import { ErweiterungConfigResponse, PermissionResponse } from "./core/base-api";
import { LoadingBarRouterModule } from "@ngx-loading-bar/router";
import { NotFoundComponent } from "./security/not-found/not-found.component";
import { AppDeciderComponent } from "./layouts/app-decider/app-decider.component";
import { ErweiterungGuard } from "./security/erweiterung.guard";
import { TooManyRequestsComponent } from "./security/too-many-requests/too-many-requests.component";
import {
  TooManyIcpGrundstueckRequestsComponent
} from './security/too-many-icp-grundstueck-requests/too-many-icp-grundstueck-requests.component';
import { InvalidRequestComponent } from "./security/invalid-request/invalidrequest.component";
import { TranslateModule } from '@ngx-translate/core';
import { LogoutComponent } from "./security/logout/logout.component";
import PermissionsEnum = PermissionResponse.PermissionsEnum;
import ActiveErweiterungenEnum = ErweiterungConfigResponse.ActiveErweiterungenEnum;


@NgModule({
  declarations: [IntercapiLayoutComponent, IntercapiPublicLayoutComponent, AppDeciderComponent, PublikationenPublicLayoutComponent, NotAuthorizedComponent, EnvironmentMarkerComponent,
    NotFoundComponent, InvalidRequestComponent],
  imports: [BrowserModule, SharedModule, TranslateModule, CoreModule, RouterModule, LoadingBarRouterModule],
  exports: [RouterModule]
})
export class AppRoutingModule {
  static forRoot(): ModuleWithProviders<AppRoutingModuleForRoot> {
    return {
      ngModule: AppRoutingModuleForRoot
    };
  }
}

const routes: Routes = [
  {
    path: '',
    redirectTo: 'home/dashboard',
    pathMatch: 'prefix',
  },
  {
    path: 'unauthorized',
    component: NotAuthorizedComponent
  },
  {
    path: 'logout',
    component: LogoutComponent,
  },
  {
    path: 'home',
    component: AppDeciderComponent,
    canActivate: [AuthorizationGuard],
    data: {
      permissions: AuthorizationGuard.routeConfig([PermissionsEnum.LOGIN, PermissionsEnum.ICPAUTHORIZED, PermissionsEnum.ANONYM])
    },
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      },
      {
        path: 'dashboard',
        loadChildren: () => import('./dashboard/dashboard.module').then(m => m.DashboardModule)
      },
      {
        path: 'notfound',
        component: NotFoundComponent,
      },
      {
        path: 'invalidrequest',
        component: InvalidRequestComponent,
      }
    ]
  },
  {
    path: 'grundstueck',
    component: AppDeciderComponent,
    canActivate: [AuthorizationGuard],
    data: {
      permissions: AuthorizationGuard.routeConfig([PermissionsEnum.LOGIN, PermissionsEnum.ICPAUTHORIZED, PermissionsEnum.ANONYM])
    },
    children: [
      {
        path: '',
        loadChildren: () => import('./grundstueck/grundstueck.module').then(m => m.GrundstueckModule)
      },
    ]
  },
  {
    path: 'grundbuch',
    component: AppDeciderComponent,
    canActivate: [AuthorizationGuard],
    data: {
      permissions: AuthorizationGuard.routeConfig([PermissionsEnum.LOGIN])
    },
    children: [
      {
        path: 'grundbuchgeschaeft',
        loadChildren: () => import('./grundbuchgeschaeft/grundbuchgeschaeft.module').then(m => m.GrundbuchgeschaeftModule)
      },
      {
        path: 'recht',
        loadChildren: () => import('./recht/recht.module').then(m => m.RechtModule)
      },
      {
        path: 'person',
        loadChildren: () => import('./person-stamm/personstamm.module').then(m => m.PersonstammModule)
      },
      {
        path: 'persongb',
        loadChildren: () => import('./person-gb/persongb.module').then(m => m.PersongbModule)
      },
      {
        path: 'grundbuchmeldung',
        loadChildren: () => import('./grundbuchmeldung/grundbuchmeldung.module').then(m => m.GrundbuchmeldungModule)
      },
      {
        path: 'altegrundbuchmeldung',
        loadChildren: () => import('./alte-grundbuchmeldung/alte-grundbuchmeldung.module').then(m => m.AlteGrundbuchmeldungModule)
      },
      {
        path: 'ech0134',
        loadChildren: () => import('./ech0134/ech0134.module').then(m => m.Ech0134Module)
      },
      {
        path: 'publikation',
        loadChildren: () => import('./publikation/publikation.module').then(m => m.PublikationModule)
      }
    ]
  },
  {
    path: 'vermessung',
    component: AppDeciderComponent,
    canActivate: [AuthorizationGuard],
    data: {
      permissions: AuthorizationGuard.routeConfig([PermissionsEnum.LOGIN])
    },
    children: [
      {
        path: 'geometergeschaeft',
        loadChildren: () => import('./geometergeschaeft/geometergeschaeft.module').then(m => m.GeometergeschaeftModule)
      },
      {
        path: 'gebaeude',
        loadChildren: () => import('./gebaeude/gebaeude.module').then(m => m.GebaeudeModule)
      },
    ]
  },
  {
    path: 'gwr',
    component: AppDeciderComponent,
    canActivate: [AuthorizationGuard],
    data: {
      permissions: AuthorizationGuard.routeConfig([PermissionsEnum.LOGIN])
    },
    children: [
      {
        path: '',
        loadChildren: () => import('./gwr/gwr.module').then(m => m.GwrModule)
      },
    ]
  },
  {
    path: 'stammdaten',
    component: AppDeciderComponent,
    canActivate: [AuthorizationGuard],
    data: {
      permissions: AuthorizationGuard.routeConfig([PermissionsEnum.LOGIN])
    },
    children: [
      {
        path: 'zentrale-personen',
        loadChildren: () => import('./person-zpv/person-zpv.module').then(m => m.PersonZpvModule)
      },
      {
        path: 'stammpersonen',
        loadChildren: () => import('./person-stamm/personstamm.module').then(m => m.PersonstammModule)
      },
      {
        path: 'gemeinde',
        loadChildren: () => import('./gemeinde/gemeinde.module').then(m => m.GemeindeModule)
      },
    ]
  },
  {
    path: 'neskoab',
    component: AppDeciderComponent,
    canActivate: [AuthorizationGuard],
    data: {
      permissions: AuthorizationGuard.routeConfig([PermissionsEnum.ANZEIGESPEZNESKOGRUNDSTUECK])
    },
    children: [
      {
        path: '',
        loadChildren: () => import('./neskoab/neskoab.module').then(m => m.NeskoabModule)
      },
    ]
  },
  {
    path: 'system',
    component: AppDeciderComponent,
    canActivate: [AuthorizationGuard],
    data: {
      permissions: AuthorizationGuard.routeConfig([PermissionsEnum.LOGIN])
    },
    children: [
      {
        path: 'konfiguration',
        canActivate: [AuthorizationGuard],
        data: {
          permissions: AuthorizationGuard.routeConfig([PermissionsEnum.CONFIGMANAGER])
        },
        loadChildren: () => import('./configuration/configuration.module').then(m => m.ConfigurationModule)
      },
      {
        path: 'import-export',
        canActivate: [AuthorizationGuard, ErweiterungGuard],
        data: {
          permissions: AuthorizationGuard.routeConfig([PermissionsEnum.DATENEXPORTANSICHT, PermissionsEnum.DATENEXPORTAUFTRAGADMIN]),
          erweiterung: ErweiterungGuard.routeConfig([ActiveErweiterungenEnum.DATENEXPORT]),
        },
        loadChildren: () => import('./batch-auftrag/batch-auftrag.module').then(m => m.BatchAuftragModule),
      },
      {
        path: 'ech0134',
        loadChildren: () => import('./ech0134/ech0134.module').then(m => m.Ech0134Module)
      },
      {
        path: 'erweitertes-suchregister',
        canActivate: [AuthorizationGuard],
        data: {
          permissions: AuthorizationGuard.routeConfig([PermissionsEnum.LOGIN]),
          erweiterung: ErweiterungGuard.routeConfig([ActiveErweiterungenEnum.ERWEITERTESSUCHREGISTER]),
        },
        loadChildren: () => import('./erweitertes-suchregister/erweitertes-suchregister.module').then(m => m.ErweitertesSuchregisterModule)
      }
    ]
  },
  {
    path: 'unterstuetzung',
    component: AppDeciderComponent,
    canActivate: [LoginGuard]
  },
  {
    path: 'security',
    component: AppDeciderComponent,
    children: [
      {
        path: 'forbidden',
        component: ForbiddenComponent
      },
      {
        path: 'unsupported',
        component: UnsupportedComponent
      },
      {
        path: 'toomanyrequests',
        component: TooManyRequestsComponent
      },
      {
        path: 'toomanyicpgrundstueckrequests',
        component: TooManyIcpGrundstueckRequestsComponent
      }
    ]
  },
  {
    path: '**',
    redirectTo: 'home/dashboard',
    pathMatch: 'prefix',
  },
];

@NgModule({
  imports: [AppRoutingModule, RouterModule.forRoot(routes, {
    onSameUrlNavigation: 'reload',
    relativeLinkResolution: 'legacy'
  }), CoreModule.forRoot()],
  exports: [AppRoutingModule],
})
export class AppRoutingModuleForRoot {
}

